// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getCookie } from "@insite/client-framework/Common/Cookies";
import caseInsensitiveSort from "@insite/client-framework/Common/Utilities/caseInsensitiveSort";
import { createHandlerChainRunner, Handler } from "@insite/client-framework/HandlerCreator";
import {
    ContentPageCollectionModel,
    ContentPageSearchParameter,
    getContentPageCollection,
} from "@mcmc/App/Services/ContentPageSearchService";
import { ContentGroupKey } from "@mcmc/App/Store/Reducers";
import { SearchTypes } from "@mcmc/App/Store/Selectors/SearchResultsSelectors";
import qs from "qs";

interface Parameter {
    queryString: string;
    isResourceListView?: boolean;
}

interface Props {
    apiParameter: ContentPageSearchParameter;
    result: ContentPageCollectionModel;
    filter: {
        articleTypes?: string[];
        category?: string;
        tag?: string;
        searchWithinQueries?: string[] | undefined;
        query: string;
        searchType: string;
    };
    contentGroup?: ContentGroupKey;
}

type HandlerType = Handler<Parameter, Props>;

export const PopulateApiParameter: HandlerType = props => {
    const { queryString, isResourceListView } = props.parameter;
    const parsedQuery = qs.parse(queryString.startsWith("?") ? queryString.substr(1) : queryString);
    const splitCommaSeparated = (value?: string | string[] | null) =>
        typeof value === "string" ? value.split(",") : undefined;
    const { searchType, query, searchWithinQueries, page, sort } = parsedQuery;

    let pageSize = parsedQuery.pageSize;

    if (!pageSize) {
        pageSize = isResourceListView ? getCookie("ResourcesList-PageSize") : getCookie("ProductList-PageSize");
    }

    let { tag, articleTypes } = parsedQuery;
    if (searchType === SearchTypes.PAGES) {
        articleTypes = undefined;
        tag = undefined;
    }

    const splittedArticleTypes = splitCommaSeparated(articleTypes);
    const splititedSearchWithin = splitCommaSeparated(searchWithinQueries);

    props.apiParameter = {
        query,
        formAggregations: searchType === SearchTypes.RESOURCES && !isResourceListView,
        contentType: searchType,
        articleTypes: splittedArticleTypes,
        searchWithin: splititedSearchWithin?.join(" "),
        tag: tag || "",
        category: parsedQuery.category || "",
        page: page ? parseInt(page as string, 10) : undefined,
        pageSize: pageSize ? parseInt(pageSize as string, 10) : undefined,
        sortType: sort,
    };
    props.filter = {
        query,
        articleTypes: splittedArticleTypes,
        searchWithinQueries: splititedSearchWithin,
        tag: tag || "",
        searchType,
    };
    props.contentGroup = isResourceListView ? "resourceList" : undefined;
};

export const DispatchSetParameter: HandlerType = (props: any) => {
    props.dispatch({
        type: "xContentPagesSearch/SetParameter",
        parameter: props.apiParameter,
        group: props.contentGroup,
    });
};

export const BeginLoadContentPages: HandlerType = (props: any) => {
    props.dispatch({
        type: "xContentPagesSearch/BeginLoadContentPages",
        parameter: props.apiParameter,
        group: props.contentGroup,
    });
};

export const RequestDataFromApi: HandlerType = async props => {
    try {
        props.result = await getContentPageCollection(props.apiParameter);
        props.result.contents.every(content => {
            content.pageSummary = content.pageSummary.replace(/(<([^>]+)>)/gi, "");
            return true;
        });
    } catch (error) {
        return error.errorMessage;
    }
};

export const SortFacets: HandlerType = ({ result }) => {
    if (!result) {
        return;
    }
    if (result.articleTypeFacets) {
        result.articleTypeFacets = caseInsensitiveSort(result.articleTypeFacets, "name");
    }
    if (result.tagFacets) {
        result.tagFacets = caseInsensitiveSort(result.tagFacets, "name");
    }
};

export const DispatchLoadContentPagesCompleted: HandlerType = (props: any) => {
    props.dispatch({
        type: "xContentPagesSearch/LoadContentPagesCompleted",
        contentPageCollectionModel: props.result,
        parameter: props.apiParameter,
        group: props.contentGroup,
    });
};

export const DispatchSetFilters: HandlerType = (props: any) => {
    if (!props.parameter.isResourceListView) {
        props.dispatch({
            type: "Pages/ProductList/SetProductFilters",
            result: props.filter,
        });
    }
};

export const chain = [
    PopulateApiParameter,
    DispatchSetParameter,
    BeginLoadContentPages,
    RequestDataFromApi,
    SortFacets,
    DispatchLoadContentPagesCompleted,
    DispatchSetFilters,
];

const loadContentPages = createHandlerChainRunner(chain, "LoadContentPages");
export default loadContentPages;
